/**
 * Application document upload helpers (presigned URL flow).
 */

import type { paths } from '@access-cms/api-types';

import { del, get, patch, post } from '../../client';

type ApplicationDocumentsPath = '/api/v1/applications/{applicationId}/documents';
type ApplicationDocumentPath = '/api/v1/applications/{applicationId}/documents/{documentId}';
type ApplicationDocumentByIdPath = '/api/v1/applications/{applicationId}/documents/{documentId}';
type ApplicationDocumentDownloadPath = '/api/v1/applications/{applicationId}/documents/{documentId}/download';

type CreateDocumentBody = paths[ApplicationDocumentsPath]['post']['requestBody']['content']['application/json'];
type CreateDocumentResponse =
  paths[ApplicationDocumentsPath]['post']['responses']['201']['content']['application/json'];
type ConfirmDocumentBody = NonNullable<
  paths[ApplicationDocumentPath]['patch']['requestBody']
>['content']['application/json'];
type ConfirmDocumentResponse =
  paths[ApplicationDocumentPath]['patch']['responses']['200']['content']['application/json'];
type ListDocumentsResponse = paths[ApplicationDocumentsPath]['get']['responses']['200']['content']['application/json'];
type GetDocumentResponse = paths[ApplicationDocumentByIdPath]['get']['responses']['200']['content']['application/json'];
type GetDocumentDownloadUrlResponse =
  paths[ApplicationDocumentDownloadPath]['get']['responses']['200']['content']['application/json'];

/** Applicant signature PNG uploaded via the staging → GuardDuty workflow */
export type SignatureDocumentUploadBody = {
  documentType: 'SIGNATURE';
  filename: string;
  mimeType: 'image/png';
  size: number;
};

export type ApplicationDocumentUploadRequestBody = CreateDocumentBody;
export type ApplicationDocumentUploadRequestData = NonNullable<CreateDocumentResponse['data']>;
export type ApplicationDocumentConfirmData = NonNullable<ConfirmDocumentResponse['data']>;
export type ApplicationDocumentDetailData = NonNullable<GetDocumentResponse['data']>;
export type ApplicationDocumentListData = NonNullable<ListDocumentsResponse['data']>;
export type ApplicationDocumentDownloadUrlData = NonNullable<GetDocumentDownloadUrlResponse['data']>;

const SIGNATURE_SCAN_POLL_INTERVAL_MS = 2000;
const SIGNATURE_SCAN_MAX_ATTEMPTS = 60;

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

export interface ApplicationDocumentUploadResult {
  applicationId: string;
  documentId: string;
  documentType: CreateDocumentBody['documentType'];
  documentLabel?: string;
  filename: string;
  mimeType: CreateDocumentBody['mimeType'];
  scanStatus: string;
  sizeBytes: number;
  uploadedAt: string;
}

export async function requestApplicationDocumentUpload(
  applicationId: string,
  body: CreateDocumentBody,
): Promise<CreateDocumentResponse> {
  return post('/api/v1/applications/{applicationId}/documents', body, {
    pathParams: { applicationId },
  });
}

export async function confirmApplicationDocumentUpload(
  applicationId: string,
  documentId: string,
): Promise<ConfirmDocumentResponse> {
  const body: ConfirmDocumentBody = { uploadConfirmed: true };

  return patch('/api/v1/applications/{applicationId}/documents/{documentId}', body, {
    pathParams: { applicationId, documentId },
  });
}

export async function listApplicationDocuments(applicationId: string): Promise<ListDocumentsResponse> {
  return get('/api/v1/applications/{applicationId}/documents', {
    pathParams: { applicationId },
  });
}

export async function getApplicationDocument(applicationId: string, documentId: string): Promise<GetDocumentResponse> {
  return get('/api/v1/applications/{applicationId}/documents/{documentId}', {
    pathParams: { applicationId, documentId },
  });
}

export async function getApplicationDocumentDownloadUrl(
  applicationId: string,
  documentId: string,
): Promise<GetDocumentDownloadUrlResponse> {
  return get('/api/v1/applications/{applicationId}/documents/{documentId}/download', {
    pathParams: { applicationId, documentId },
  });
}

export async function deleteApplicationDocument(applicationId: string, documentId: string): Promise<void> {
  await del('/api/v1/applications/{applicationId}/documents/{documentId}', {
    pathParams: { applicationId, documentId },
  });
}

export async function waitForApplicationDocumentClean(
  applicationId: string,
  documentId: string,
): Promise<ApplicationDocumentDetailData> {
  for (let attempt = 0; attempt < SIGNATURE_SCAN_MAX_ATTEMPTS; attempt += 1) {
    const response = await getApplicationDocument(applicationId, documentId);
    const document = response.data;

    if (!document) {
      throw new Error('Document not found');
    }

    if (document.scanStatus === 'CLEAN') {
      return document;
    }

    if (document.scanStatus === 'INFECTED' || document.scanStatus === 'ERROR') {
      throw new Error(`Document scan failed with status ${document.scanStatus}`);
    }

    await delay(SIGNATURE_SCAN_POLL_INTERVAL_MS);
  }

  throw new Error('Document malware scan timed out. Please try again.');
}

export async function uploadApplicationDocumentToS3(
  uploadUrl: string,
  file: File,
  mimeType: CreateDocumentBody['mimeType'],
): Promise<void> {
  const response = await fetch(uploadUrl, {
    body: file,
    headers: {
      'Content-Type': mimeType,
    },
    method: 'PUT',
  });

  if (!response.ok) {
    const responseBody = await response.text().catch(() => '');
    console.error('[ApplicationDocument] S3 PUT failed', {
      bodySnippet: responseBody.slice(0, 500),
      contentType: mimeType,
      fileSize: file.size,
      status: response.status,
      statusText: response.statusText,
    });
    throw new Error(`Document upload to S3 failed (${response.status} ${response.statusText})`);
  }
}

export async function uploadApplicationDocument({
  applicationId,
  body,
  file,
}: {
  applicationId: string;
  body: CreateDocumentBody;
  file: File;
}): Promise<ApplicationDocumentUploadResult> {
  let documentId: string | undefined;

  try {
    const uploadRequest = await requestApplicationDocumentUpload(applicationId, body);
    const uploadData = uploadRequest.data;

    if (!uploadData?.uploadUrl || !uploadData.id) {
      throw new Error('Document upload request did not return an upload URL and document ID');
    }

    documentId = uploadData.id;
    await uploadApplicationDocumentToS3(uploadData.uploadUrl, file, body.mimeType);

    const confirmResponse = await confirmApplicationDocumentUpload(applicationId, uploadData.id);
    const confirmedDocument = confirmResponse.data;

    return {
      applicationId,
      documentId: uploadData.id,
      documentLabel: body.label,
      documentType: body.documentType,
      filename: uploadData.filename ?? body.filename,
      mimeType: body.mimeType,
      scanStatus: confirmedDocument?.scanStatus ?? uploadData.scanStatus ?? 'PENDING',
      sizeBytes: uploadData.sizeBytes ?? body.size,
      uploadedAt: confirmedDocument?.updatedAt ?? new Date().toISOString(),
    };
  } catch (error) {
    console.error('[ApplicationDocument] Application document upload flow failed', {
      applicationId,
      documentId,
      documentType: body.documentType,
      error: error instanceof Error ? error.message : String(error),
      filename: body.filename,
    });

    if (documentId) {
      try {
        await deleteApplicationDocument(applicationId, documentId);
      } catch (cleanupError) {
        console.error('[ApplicationDocument] Failed to clean up document upload reservation', {
          applicationId,
          cleanupError: cleanupError instanceof Error ? cleanupError.message : String(cleanupError),
          documentId,
        });
      }
    }

    throw error;
  }
}
